import { type LokiFields, postLokiRecord } from './loki-transport';

/** Loki log keys for the CBCC (travel-credit-card) flow. */
export const CbccLokiKey = {
	/** CVP resolved on the landing page — carries the per-user CTA state. */
	CVP_LOADED: 'CBCC_CVP_LOADED',
	/** Apply CTA clicked but userFlow was null/unrecognized → dispatch no-op. */
	CTA_NOOP: 'CBCC_CTA_NOOP',
	/** Post-login auto-resume fired: the user logged in via a CTA and we
	 *  dispatched their flow. `source` is 'loader' (loader flow was ready),
	 *  'refetch' (fetched CVP directly past the invalidate→commit race), or
	 *  'refetch-failed'. Diagnoses the "no auto-redirect after login" path. */
	POST_LOGIN_RESUME: 'CBCC_POST_LOGIN_RESUME',
	/** Hero transition clips' readiness when the user reaches the video region,
	 *  plus viewport + connection — diagnoses the first-load carousel/video pop
	 *  (was the asset downloaded in time on this device + connection?). */
	MEDIA_STATUS: 'CBCC_MEDIA_STATUS',
	/** The /travel-credit-card route threw at render (most often a failed dynamic
	 *  import of the route's own code-split chunk — transient network or a stale
	 *  chunk after deploy). The route's errorComponent caught it, showed a
	 *  fallback, and logged this so the otherwise-silent crash is diagnosable. */
	ROUTE_ERROR: 'CBCC_ROUTE_ERROR',
} as const;

type CbccLokiKey = (typeof CbccLokiKey)[keyof typeof CbccLokiKey];

/**
 * Info-level CBCC page-load log to `/logs/ui`, fired on EVERY landing-page load
 * (client-side). Always records whether the user is logged in; when they are,
 * also the CVP-resolved per-user state (status / userFlow / ctaText). Fires
 * AGAIN after an in-page login once the fresh CVP arrives (the loader re-runs on
 * router.invalidate(), so the data changes). Fire-and-forget — never throws,
 * never blocks render.
 *  - Logged out: `{ isLoggedIn: false }` (no CVP yet — the loader is `at`-gated).
 *  - Logged in:  `{ isLoggedIn: true, status, userFlow, ctaText }`.
 */
export function logCbccCvp(fields: {
	isLoggedIn: boolean;
	status?: string;
	userFlow?: string | null;
	ctaText?: string;
}): void {
	logCbccLokiInfo(CbccLokiKey.CVP_LOADED, fields);
}

export function logCbccLokiInfo(key: CbccLokiKey, fields?: LokiFields): void {
	postLokiRecord('info', key, fields);
}

/**
 * Error-level CBCC log to `/logs/ui`. Used by the route errorComponent so a
 * caught render/chunk failure is visible in prod instead of a silent crash.
 * Classifies the common "failed to fetch dynamically imported module" case
 * (a code-split chunk that didn't load) so it's easy to filter. Fire-and-forget.
 */
export function logCbccLokiError(
	key: CbccLokiKey,
	error: unknown,
	fields?: LokiFields,
): void {
	const message =
		error instanceof Error ? error.message : String(error ?? 'unknown');
	// `import()` failures surface with these messages across browsers
	const isChunkLoadError =
		/dynamically imported module|importing a module script|Failed to fetch/i.test(
			message,
		);
	postLokiRecord('error', key, {
		message,
		isChunkLoadError,
		...fields,
	});
}
